import {
  ArchiveIcon,
  BoxArrowUpIcon,
  DownloadSimpleIcon,
  EyeIcon,
  HandArrowDownIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { ChangeEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useDownloadPackagesCSV } from '@/api/packages/download-packages-csv'
import { useGetPackagesCountsByClient } from '@/api/packages/get-packages-counts-by-client'
import {
  getPackagesDashboard,
  getPackagesQueryKey,
  PACKAGES_BASE_QUERY_KEY,
  useGetPackagesDashboard,
} from '@/api/packages/get-packages-dashboard'
import { useGetPackagesSettings } from '@/api/packages/get-packages-settings'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { Select, SelectOption } from '@/components/ui/select'
import { StatusTabOption, StatusTabs } from '@/components/ui/status-tabs'
import { BulkAction, RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { Typography } from '@/components/ui/typography'
import { popupFactory, toastFactory } from '@/factories'
import { useCaptureEvent, useFilters, useSearchParams } from '@/hooks'
import {
  DateRange,
  LocationOption,
  LockedResident,
  PackageModel,
  PackageStatusSchema,
  UserRole,
  UserRoleEnum,
} from '@/models'
import { getPackageBadgeColor } from '@/pages/packages'
import { ArchivePackageModal } from '@/pages/packages/archive-package-modal'
import { BulkArchiveModal } from '@/pages/packages/bulk-archive-modal'
import { BulkCheckoutModal } from '@/pages/packages/bulk-checkout-modal'
import { BulkDeleteModal } from '@/pages/packages/bulk-delete-modal'
import { BulkUnarchiveModal } from '@/pages/packages/bulk-unarchive-modal'
import { CheckoutPackageModal } from '@/pages/packages/checkout-package-modal/checkout-package-modal'
import { DeletePackageModal } from '@/pages/packages/delete-package-modal'
import { PackageDetailsModal } from '@/pages/packages/package-details-modal'
import { PackageFormModal } from '@/pages/packages/package-form-modal'
import { getCarrierOptions } from '@/pages/packages/package-form-modal/package-form-modal.constants'
import { useDefaultFiltering } from '@/pages/packages/packages-list/packages-list.utils'
import { UnarchivePackageModal } from '@/pages/packages/unarchive-package-modal'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { Building } from '@/reducers/types'
import { selectUserRole } from '@/reducers/userDataReducer'
import { buildCsvFilename, downloadCsv, getApiErrorMessage } from '@/utils'

import {
  ADDITIONAL_FILTERS_DROPDOWN_WIDTH,
  getPackageColumns,
  getPerishableFilterOptions,
  initialPackagesFilters,
  PACKAGES_LIST_ID,
  PackagesFilters,
  packagesQueryFiltersSchema,
} from './packages-list.constants'
import { PackageStatusExtended, PackageStatusExtendedEnum } from './packages-list.types'

export interface PackagesListPageProps {
  // Note: when set, scopes the table to a single resident (e.g. embedded in the Resident
  // Profile) - the resident filter is applied directly to the queries below and is never
  // stored in `useFilters`/the URL, so it can't be surfaced, edited, or cleared in the UI.
  // Also used to pre-select and lock the resident field on the "New Package" CTA.
  resident?: LockedResident
  // Note: overrides the globally-selected building (Redux) with a specific one - needed so an
  // embedded resident's own building is used even if a PM has a different building selected
  // in the top nav.
  building?: Building | null
}

export const PackagesListPage = ({ resident, building }: PackagesListPageProps = {}) => {
  const { t } = useTranslation(['packages'])
  const residentId = resident?.id
  const globalBuilding = useSelector(selectActiveBuilding)
  const selectedBuilding = building ?? globalBuilding
  const buildingId = selectedBuilding?.id
  const userRole = useSelector((state) => selectUserRole(state)) as UserRole
  const { captureEvent, captureDebouncedEvent } = useCaptureEvent()
  const [residentsSearch, setResidentsSearch] = useState('')
  const queryClient = useQueryClient()

  const searchParams = useSearchParams({
    initialState: initialPackagesFilters,
    schema: packagesQueryFiltersSchema,
  })

  const filters = useFilters<PackagesFilters>({
    controlledState: searchParams.parsedQuery,
    initialState: initialPackagesFilters,
    onFiltersChange: searchParams.writeParams,
  })

  // Note: gates the queries below so they don't fire once before the default date-range/sort
  // are resolved and again right after - see useDefaultFiltering.
  const areDefaultsResolved = useDefaultFiltering({ filters, searchParams })

  const timezone = selectedBuilding?.timezone || 'America/Chicago'

  const fromDateFilter = filters.getFilter('fromDate')
  const toDateFilter = filters.getFilter('toDate')
  const dateRange: DateRange = [
    fromDateFilter ? new Date(fromDateFilter) : null,
    toDateFilter ? new Date(toDateFilter) : null,
  ]
  const locationsFilter = filters.getFilter('locations')
  const rawSearch = filters.getFilter('search')
  const search = rawSearch?.trim()
  const status = filters.getFilter('status')
  const sortBy = filters.getFilter('sortBy')
  const sortOrder = filters.getFilter('sortOrder')
  const apiSort = sortBy && sortOrder ? `${sortBy}:${sortOrder}` : undefined
  const selectedPerishable = filters.getFilter('perishable')

  // Note: Skip 'all' status which is only used in UI, but doesn't exist in the API
  const apiStatus = status === PackageStatusExtendedEnum.enum.All ? undefined : status

  const { data: packagesSettings } = useGetPackagesSettings({ buildingId: buildingId ?? 0 })

  const {
    data: packagesData,
    error,
    isFetching,
  } = useGetPackagesDashboard({
    buildingId: areDefaultsResolved ? (buildingId ?? 0) : 0,
    carrier: filters.getFilter('carrier'),
    clientId: residentId ? [residentId] : filters.getFilter('clientId'),
    endDate: toDateFilter,
    limit: filters.getFilter('limit'),
    locations: filters.getFilter('locations'),
    perishable: selectedPerishable,
    search,
    skip: filters.getFilter('skip'),
    sort: apiSort,
    startDate: fromDateFilter,
    status: apiStatus,
  })

  // Note: only needed to build the "Resident" filter's options - not rendered at all when
  // `residentId` is set, so skip the fetch entirely (buildingId: undefined disables the query).
  // Also gated on `areDefaultsResolved`, same reason as the dashboard query above.
  // This is the residents dropdown's own list, independent of the packages table's
  // pagination - `skip` here must stay 0 rather than `filters.getFilter('skip')` (the table's
  // row offset), or paging the table would silently change which residents show up here.
  const { data: residentsData, isFetching: isResidentsFetching } = useGetPackagesCountsByClient({
    buildingId: residentId || !areDefaultsResolved ? undefined : buildingId,
    carrier: filters.getFilter('carrier'),
    fromDate: fromDateFilter,
    limit: 25,
    locations: filters.getFilter('locations'),
    perishable: filters.getFilter('perishable'),
    search: residentsSearch,
    skip: 0,
    status: apiStatus,
    toDate: toDateFilter,
  })

  const residents = residentsData?.data

  const { mutateAsync: downloadCSV } = useDownloadPackagesCSV()

  const packages = packagesData?.results || []
  const packagesFilterCounters = {
    count: packagesData?.count || 0,
    countArchived: packagesData?.countArchived || 0,
    countPending: packagesData?.countPending || 0,
    countReceived: packagesData?.countReceived || 0,
    countRequested: packagesData?.countRequested || 0,
  }

  const isCSVButtonDisabled = !packages.length

  const handleDownloadCSV = async () => {
    if (!selectedBuilding) return

    try {
      const blob = await downloadCSV({
        buildingId: selectedBuilding.id,
        endDate: toDateFilter,
        limit: filters.getFilter('limit'),
        locations: filters.getFilter('locations'),
        search,
        skip: filters.getFilter('skip'),
        sort: apiSort,
        startDate: fromDateFilter,
        status: apiStatus,
      })

      downloadCsv({
        blob,
        filename: buildCsvFilename({
          building: selectedBuilding.name,
          dateRange,
          page: 'packages',
          timezone,
        }),
      })

      captureEvent('user_action', {
        name: 'packages_csv_download_click',
        screen: PACKAGES_LIST_ID,
      })
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleCreatePackage = () => {
    popupFactory.open(PackageFormModal, {
      building: selectedBuilding,
      lockResident: resident,
      mode: 'create',
      packageData: null,
    })
  }

  const isPM =
    userRole === UserRoleEnum.enum.PropertyManager || userRole === UserRoleEnum.enum.Administrator
  const isFM = userRole === UserRoleEnum.enum.ShiftManager
  const hasBulkAccess = isPM || isFM

  const bulkActions: BulkAction<PackageModel>[] = useMemo(() => {
    if (!hasBulkAccess) return []

    if (status === PackageStatusSchema.enum.Archived) {
      return [
        ...(isPM
          ? [
              {
                icon: TrashIcon,
                onClick: (items: PackageModel[]) => {
                  popupFactory.open(BulkDeleteModal, { packages: items })
                },
                onlyBox: true,
                use: 'danger' as const,
              },
            ]
          : []),
        {
          icon: BoxArrowUpIcon,
          label: t('packages:list.bulkActions.unarchive'),
          onClick: (items) => {
            popupFactory.open(BulkUnarchiveModal, { packages: items })
          },
        },
      ]
    }

    const getAlreadyReceivedCount = (items: PackageModel[]) =>
      items.filter(
        (pkg) =>
          pkg.status === PackageStatusSchema.enum.Received ||
          pkg.status === PackageStatusSchema.enum.Archived,
      ).length

    const cannotCheckout = (items: PackageModel[]) => getAlreadyReceivedCount(items) > 0

    const actions: BulkAction<PackageModel>[] = []

    actions.push(
      {
        icon: ArchiveIcon,
        label: t('packages:list.bulkActions.archive'),
        onClick: (items) => {
          popupFactory.open(BulkArchiveModal, { packages: items })
        },
        use: 'warning',
      },
      {
        disabled: cannotCheckout,
        icon: HandArrowDownIcon,
        label: t('packages:list.bulkActions.checkout'),
        onClick: (items) => {
          popupFactory.open(BulkCheckoutModal, { packages: items })
        },
        tooltip: (items) => {
          const count = getAlreadyReceivedCount(items)
          if (!count) return undefined
          return t('packages:list.bulkActions.checkoutTooltip', { count })
        },
      },
    )

    return actions
  }, [hasBulkAccess, isPM, status, t])

  const rowActions: RowAction<PackageModel>[] = useMemo(
    () => [
      {
        icon: EyeIcon,
        label: t('packages:list.rowActions.details'),
        onClick: (item) => {
          popupFactory.open(PackageDetailsModal, {
            packageData: item,
          })
        },
        primary: true,
      },
      {
        hidden: (item) => item.status === PackageStatusSchema.enum.Archived,
        icon: PencilIcon,
        label: t('packages:list.rowActions.edit'),
        onClick: (item) => {
          popupFactory.open(PackageFormModal, {
            mode: 'edit',
            packageData: item,
          })
        },
      },
      {
        hidden: (item) =>
          item.status === PackageStatusSchema.enum.Received ||
          item.status === PackageStatusSchema.enum.Archived,
        icon: HandArrowDownIcon,
        label: t('packages:list.rowActions.checkout'),
        onClick: (item) => {
          popupFactory.open(CheckoutPackageModal, {
            selectedPackageData: item,
          })
        },
      },
      {
        hidden: (item) => item.status === PackageStatusSchema.enum.Archived,
        icon: ArchiveIcon,
        iconSize: 'medium',
        label: t('packages:list.rowActions.archive'),
        onClick: (item) => {
          popupFactory.open(ArchivePackageModal, {
            packageId: item.id,
          })
        },
        variant: 'warning',
      },
      {
        hidden: (item) =>
          item.status !== PackageStatusSchema.enum.Archived ||
          (userRole !== 'PropertyManager' &&
            userRole !== 'ShiftManager' &&
            userRole !== 'Administrator'),
        icon: BoxArrowUpIcon,
        label: t('packages:list.rowActions.unarchive'),
        onClick: (item) => {
          popupFactory.open(UnarchivePackageModal, {
            packageId: item.id,
          })
        },
      },
      {
        hidden: (item) =>
          item.status !== PackageStatusSchema.enum.Archived ||
          (userRole !== 'PropertyManager' && userRole !== 'Administrator'),
        icon: TrashIcon,
        label: t('packages:list.rowActions.delete'),
        onClick: (item) => {
          popupFactory.open(DeletePackageModal, {
            packageId: item.id,
          })
        },
        variant: 'danger',
      },
    ],
    [userRole, t],
  )

  const residentColumnSettings = usePersonColumnSettings({
    column: 'resident',
    dashboard: 'Packages',
    fieldType: 'resident',
    rowsQueryKey: [PACKAGES_BASE_QUERY_KEY],
  })
  const checkedInColumnSettings = usePersonColumnSettings({
    column: 'checkedIn',
    dashboard: 'Packages',
    fieldType: 'staff',
    rowsQueryKey: [PACKAGES_BASE_QUERY_KEY],
  })
  const checkedOutColumnSettings = usePersonColumnSettings({
    column: 'checkedOut',
    dashboard: 'Packages',
    fieldType: 'staff',
    rowsQueryKey: [PACKAGES_BASE_QUERY_KEY],
  })

  const tableColumns = getPackageColumns({
    checkedInColumnSettings,
    checkedOutColumnSettings,
    residentColumnSettings,
    selectedBuilding,
    t,
    timezone,
  })

  const statusTabsOptions: StatusTabOption<PackageStatusExtended>[] = useMemo(() => {
    const allTab = {
      count: packagesFilterCounters.count,
      label: t('packages:status.all'),
      value: PackageStatusExtendedEnum.enum.All,
    }
    const pendingTab = {
      color: getPackageBadgeColor(PackageStatusSchema.enum.Pending),
      count: packagesFilterCounters.countPending,
      label: t('packages:status.pending'),
      value: PackageStatusSchema.enum.Pending,
    }
    const requestedTab = {
      color: getPackageBadgeColor(PackageStatusSchema.enum.Requested),
      count: packagesFilterCounters.countRequested,
      label: t('packages:status.requested'),
      value: PackageStatusSchema.enum.Requested,
    }
    const receivedTab = {
      color: getPackageBadgeColor(PackageStatusSchema.enum.Received),
      count: packagesFilterCounters.countReceived,
      label: t('packages:status.received'),
      value: PackageStatusSchema.enum.Received,
    }
    const archivedTab = {
      color: getPackageBadgeColor(PackageStatusSchema.enum.Archived),
      count: packagesFilterCounters.countArchived,
      label: t('packages:status.archived'),
      value: PackageStatusSchema.enum.Archived,
    }

    if (packagesSettings?.d2dMode) {
      return [allTab, pendingTab, requestedTab, receivedTab, archivedTab]
    }

    return [allTab, pendingTab, receivedTab, archivedTab]
  }, [packagesFilterCounters, packagesSettings?.d2dMode, t])

  const paginationTotal = useMemo(() => {
    if (!packagesData) return 0

    switch (status) {
      case 'Pending':
        return packagesData.countPending
      case 'Requested':
        return packagesData.countRequested
      case 'Received':
        return packagesData.countReceived
      case PackageStatusSchema.enum.Archived:
        return packagesData.countArchived
      default:
        return packagesData.count
    }
  }, [status, packagesData])

  const resolveAllMatchingPackages = async (signal?: AbortSignal): Promise<PackageModel[]> => {
    if (!buildingId || !paginationTotal) return []

    const request = {
      buildingId,
      carrier: filters.getFilter('carrier'),
      clientId: residentId ? [residentId] : filters.getFilter('clientId'),
      endDate: toDateFilter,
      limit: paginationTotal,
      locations: filters.getFilter('locations'),
      perishable: selectedPerishable,
      search: filters.getFilter('search'),
      skip: 0,
      sort: apiSort,
      startDate: fromDateFilter,
      status: apiStatus,
    }

    const response = await queryClient.fetchQuery({
      queryFn: ({ signal: querySignal }) => getPackagesDashboard(request, querySignal ?? signal),
      queryKey: getPackagesQueryKey(request),
    })

    return response.results
  }

  const locationOptions: SelectOption<string>[] =
    packagesSettings?.locations.map((location) => ({
      label: location.name,
      value: String(location.id),
    })) || []

  const residentsOptions: SelectOption<number>[] =
    residents?.map((client) => ({
      label: `${client.name} - (${client.count})`,
      value: client.clientId,
    })) || []

  const selectedLocations: LocationOption[] = useMemo(
    () => (locationsFilter ?? []).map((value) => ({ label: value, value })),
    [locationsFilter],
  )

  const selectedLocationValues = useMemo(
    () => selectedLocations.map((loc) => loc.value),
    [selectedLocations],
  )

  const handleLocationChange = (values: string[]) => {
    filters.setFilter('locations', values)
    captureEvent('user_action', {
      name: 'location_change',
      screen: PACKAGES_LIST_ID,
      value: values,
    })
  }

  const handleDateChange = (range: DateRange) => {
    const fromDate = range[0]?.getTime()
    const toDate = range[1]?.getTime()
    filters.setFilters({
      fromDate,
      toDate,
    })
    captureEvent('user_action', {
      fromDate,
      name: 'time_date_range_change',
      screen: PACKAGES_LIST_ID,
      toDate,
    })
  }

  const handleSort = (sortBy: string | null, sortOrder: SortOrder | null) => {
    filters.setFilters({
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
    })
    captureEvent('user_action', {
      name: 'sort_change',
      screen: PACKAGES_LIST_ID,
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
    })
  }

  const handleStatusChange = (status: PackageStatusExtended) => {
    filters.setFilter('status', status)
    captureEvent('user_action', {
      name: 'status_change',
      screen: PACKAGES_LIST_ID,
      value: status,
    })
  }

  const handlePerishableChange = (perishable?: string) => {
    filters.setFilter('perishable', perishable)
    captureEvent('user_action', {
      name: 'perishable_change',
      screen: PACKAGES_LIST_ID,
      value: perishable,
    })
  }

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
    captureDebouncedEvent('user_action', {
      name: 'search_change',
      screen: PACKAGES_LIST_ID,
      value: e.target.value,
    })
  }

  const handleAssigneeChange = (assignees: number[]) => {
    filters.setFilter('clientId', assignees)
    captureEvent('user_action', {
      name: 'clientId_change',
      screen: PACKAGES_LIST_ID,
      value: assignees,
    })
  }

  const handleAssigneeReset = () => {
    filters.setFilter('clientId', [])
    captureEvent('user_action', {
      name: 'clientId_change',
      screen: PACKAGES_LIST_ID,
      value: [],
    })
  }

  const handleCarrierChange = (carriers: string[]) => {
    filters.setFilter('carrier', carriers)
    captureEvent('user_action', {
      name: 'carrier_change',
      screen: PACKAGES_LIST_ID,
      value: carriers,
    })
  }

  const handleCarrierReset = () => {
    filters.setFilter('carrier', undefined)
    captureEvent('user_action', {
      name: 'carrier_change',
      screen: PACKAGES_LIST_ID,
      value: undefined,
    })
  }

  return (
    <TableGroup
      id={PACKAGES_LIST_ID}
      error={error}
      loading={isFetching}
      columns={tableColumns}
      data={packages}
      mode={hasBulkAccess ? 'bulk' : 'regular'}
      bulkActions={bulkActions}
      selectAllMatching={{ resolveAll: resolveAllMatchingPackages, total: paginationTotal }}
      // Note: Resident Profile embed - status tabs row is already in use, so title + "New
      // Package" get their own row above it, inside the same card (see EL-14186 Figma).
      title={
        residentId && (
          <Typography as="h2" size="heading-small" color="black800">
            {t('packages:page.title')}
          </Typography>
        )
      }
      titleActions={
        residentId && (
          <Button size="small" onClick={handleCreatePackage}>
            {t('packages:page.createPackage')}
            <Icon icon={PlusIcon} />
          </Button>
        )
      }
      emptyWithFilters={{
        description: t('packages:list.empty.description'),
        title: t('packages:list.empty.title'),
      }}
      getRowKey={(packageItem) => String(packageItem.id)}
      noShadow={!!residentId}
      filters={
        <>
          {residentId && (
            <DatePickerInput
              mode="range"
              placeholder={t('packages:list.filters.timeRange.placeholder')}
              size="small"
              value={dateRange}
              maxDate={new Date()}
              onRangeChange={handleDateChange}
            />
          )}
          {!residentId && (
            <Select
              searchable
              options={residentsOptions}
              size="small"
              type="multi"
              value={filters.getFilter('clientId') ?? []}
              dropdownOptions={{ width: ADDITIONAL_FILTERS_DROPDOWN_WIDTH }}
              loading={isResidentsFetching}
              placeholder={t('packages:list.filters.resident.placeholder')}
              onChange={handleAssigneeChange}
              onReset={handleAssigneeReset}
              onSearchChange={setResidentsSearch}
            />
          )}
          <Select
            searchable
            size="small"
            type="multi"
            value={filters.getFilter('carrier') ?? []}
            options={getCarrierOptions(t)}
            placeholder={t('packages:list.filters.carrier.placeholder')}
            onChange={handleCarrierChange}
            onReset={handleCarrierReset}
          />
          <Select
            name="perishable"
            options={getPerishableFilterOptions(t)}
            placeholder={t('packages:list.filters.perishable.placeholder')}
            size="small"
            value={selectedPerishable ?? null}
            onChange={handlePerishableChange}
            onReset={() => handlePerishableChange()}
          />
          <Select
            type="multi"
            name="locations"
            options={locationOptions}
            placeholder={t('packages:list.filters.location.placeholder')}
            size="small"
            value={selectedLocationValues}
            dropdownOptions={{ width: ADDITIONAL_FILTERS_DROPDOWN_WIDTH }}
            onChange={handleLocationChange}
            onReset={() => handleLocationChange([])}
          />
        </>
      }
      navigation={{
        // Note: the CSV export endpoint has no resident-scoping support (see
        // `useDownloadPackagesCSV`) - exporting here would leak the whole building's packages
        // rather than just this resident's, so it's hidden entirely until that's supported.
        actions: residentId ? undefined : (
          <Button
            onlyBox
            use="secondary"
            size="small"
            title={t('packages:list.exportCsv')}
            disabled={isCSVButtonDisabled}
            onClick={handleDownloadCSV}
          >
            <Icon icon={DownloadSimpleIcon} />
          </Button>
        ),
        // Note: Resident Profile embed moves the date range into the additional filters row
        // below (see EL-14186 Figma) - space constraints on the narrower embedded layout.
        datePicker: residentId ? undefined : (
          <DatePickerInput
            mode="range"
            placeholder={t('packages:list.filters.timeRange.placeholder')}
            size="small"
            value={dateRange}
            maxDate={new Date()}
            onRangeChange={handleDateChange}
          />
        ),
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            placeholder={t('packages:list.filters.search.placeholder')}
            size="small"
            value={rawSearch}
            onChange={handleSearchChange}
          />
        ),
        statusTabs: (
          <StatusTabs
            options={statusTabsOptions}
            value={status ?? null}
            onChange={handleStatusChange}
          />
        ),
      }}
      pagination={{
        paginationFilters: filters,
        total: paginationTotal,
      }}
      rowActions={rowActions}
      sortBy={filters.getFilter('sortBy')}
      sortOrder={filters.getFilter('sortOrder')}
      tableFilters={filters}
      onSort={handleSort}
    />
  )
}
